<script setup>
import { onMounted, ref, watch, computed } from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Servicios / APIs
import { getAutosuggest, getReverseGeocode, getRoute } from '@/services/hereApi';
import { getAutocompleteUser } from '@/services/userService';
import { createRide } from '@/services/tripService';

// Componentes
import FilterDriverComponent from '@/components/pages/admin/trip/components/FilterDriverComponent.vue';
import RequiredComponent from '@/components/utils/RequiredComponent.vue';
import LoadingComponent from '@/components/utils/LoadingComponent.vue';
import BackButtonComponent from '@/components/utils/BackButtonComponent.vue';

// Utils
import { formatISODate } from '@/globals/utils/formatDate';
import { customToast } from '@/globals/utils/customToast';
import DefaultButton from '@/components/utils/DefaultButton.vue';

// --- ESTADOS / REFS
const pickupAddress = ref('Southwest Florida Int\'l Airport (RSW)');
const pickupLatitude = ref(26.53375458982706);
const pickupLongitude = ref(-81.7595054497816);

const destinationAddress = ref('');
const destinationLatitude = ref(0.0);
const destinationLongitude = ref(0.0);

const price = ref(0.0);
const paymentMethod = ref('cash');
const distance = ref(0.0);
const duration = ref(0.0);
const durationForHumans = ref('');
const clientName = ref('');
const clientPhone = ref('');
const clientId = ref('');
const polyline = ref('');
const selectedDriver = ref({});
const bookingDate = ref('');
const notifyNearbyDrivers = ref(false);

const loading = ref(false);

// Reglas de validación
const validateRequiredInputs = {
  pickupAddress: { required: true, message: 'Pickup address is required' },
  pickupLatitude: { required: true, message: 'Pickup latitude is required' },
  pickupLongitude: { required: true, message: 'Pickup longitude is required' },
  destinationAddress: { required: true, message: 'Destination address is required' },
  destinationLatitude: { required: true, message: 'Destination latitude is required' },
  destinationLongitude: { required: true, message: 'Destination longitude is required' },
  price: { required: true, type: 'number', message: 'Price is required' },
  paymentMethod: { required: true, message: 'Payment method is required' },
  clientName: { required: true, message: 'Client name is required' },
  clientPhone: { required: true, message: 'Client phone is required' },
  bookingDate: { required: false, message: '' },
};

const fieldErrors = ref({});
const touchedFields = ref({});

// Función para obtener el valor de un campo por su nombre (para validaciones)
const getFieldValue = (fieldName) => {
  switch (fieldName) {
    case 'pickupAddress':
      return pickupAddress.value;
    case 'pickupLatitude':
      return pickupLatitude.value;
    case 'pickupLongitude':
      return pickupLongitude.value;
    case 'destinationAddress':
      return destinationAddress.value;
    case 'destinationLatitude':
      return destinationLatitude.value;
    case 'destinationLongitude':
      return destinationLongitude.value;
    case 'price':
      return price.value;
    case 'paymentMethod':
      return paymentMethod.value;
    case 'clientName':
      return clientName.value;
    case 'clientPhone':
      return clientPhone.value;
    case 'bookingDate':
      return bookingDate.value;
    default:
      return null;
  }
};

// Validación de campo individual
const validateField = (fieldName) => {
  const rule = validateRequiredInputs[fieldName];
  const value = getFieldValue(fieldName);

  let error = '';
  if (rule.required && (!value || (typeof value === 'number' && value === 0))) {
    error = rule.message;
  } else if (rule.type === 'number' && isNaN(Number(value))) {
    error = `${fieldName} must be a number`;
  }
  fieldErrors.value[fieldName] = error;
};

// Manejo de input en tiempo real
const handleInput = (fieldName) => {
  if (touchedFields.value[fieldName]) {
    validateField(fieldName);
  }
};

// Comprobamos si se debe deshabilitar el botón de crear
const isCreateTripDisabled = computed(() => {
  return Object.keys(validateRequiredInputs).some((fieldName) => {
    validateField(fieldName);
    return fieldErrors.value[fieldName];
  });
});

// --- MAPA
let map = null;
const mapContainer = ref(null);
const showMap = ref(false);
const mapLoading = ref(false);
let currentMarker = null;
const selectedIsPickup = ref(true);
const selectedLocation = ref({ lat: 0, lng: 0, address: '' });

// --- SUGERENCIAS DE DIRECCIONES
const suggestions = ref([]);
const searchingSuggestions = ref(false);
let debounceTimeout = null;

const activeInputType = ref('');
const suggestionsVisible = ref(false);

const searchSuggestions = (type, query) => {
  if (query.length < 3) {
    suggestions.value = [];
    return;
  }
  clearTimeout(debounceTimeout);
  debounceTimeout = setTimeout(async () => {
    searchingSuggestions.value = true;
    try {
      suggestions.value = await getAutosuggest(query);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      searchingSuggestions.value = false;
    }
  }, 300);
};

const showSuggestions = (type) => {
  activeInputType.value = type;
  suggestionsVisible.value = true;
};

const hideSuggestions = () => {
  suggestionsVisible.value = false;
  suggestions.value = [];
  activeInputType.value = '';
};

const selectSuggestion = (suggestion) => {
  if (activeInputType.value === 'pickup') {
    pickupAddress.value = suggestion.title;
    pickupLatitude.value = suggestion.position.lat;
    pickupLongitude.value = suggestion.position.lng;
    validateField('pickupAddress');
    validateField('pickupLatitude');
    validateField('pickupLongitude');
  } else {
    destinationAddress.value = suggestion.title;
    destinationLatitude.value = suggestion.position.lat;
    destinationLongitude.value = suggestion.position.lng;
    validateField('destinationAddress');
    validateField('destinationLatitude');
    validateField('destinationLongitude');
  }
  hideSuggestions();
};

// --- SUGERENCIAS DE USUARIOS
const userSuggestions = ref([]);
const searchingUserSuggestions = ref(false);
const activeUserInputType = ref('');
const userSuggestionsVisible = ref(false);

const searchUserSuggestions = (type, query) => {
  if (query.length < 3) {
    userSuggestions.value = [];
    return;
  }
  clearTimeout(debounceTimeout);
  debounceTimeout = setTimeout(async () => {
    searchingUserSuggestions.value = true;
    try {
      userSuggestions.value = await getAutocompleteUser(query);
    } catch (error) {
      console.error('Error fetching user suggestions:', error);
    } finally {
      searchingUserSuggestions.value = false;
    }
  }, 300);
};

const showUserSuggestions = (type) => {
  activeUserInputType.value = type;
  userSuggestionsVisible.value = true;
};

const hideUserSuggestions = () => {
  userSuggestionsVisible.value = false;
  userSuggestions.value = [];
  activeUserInputType.value = '';
};

const selectUserSuggestion = (suggestion) => {
  clientName.value = suggestion.name;
  clientPhone.value = suggestion.phone;
  clientId.value = suggestion._id;
  validateField('clientName');
  validateField('clientPhone');
  hideUserSuggestions();
};

// --- MAPA: seleccionar ubicación
const pickOnMap = (type, isPickup = true) => {
  selectedIsPickup.value = isPickup;
  showMap.value = true;
  initMap();
};

const initMap = () => {
  setTimeout(() => {
    if (map) {
      map.remove();
    }

    let initialLat = selectedIsPickup.value ? pickupLatitude.value || 26.517938 : destinationLatitude.value || 26.517938;
    let initialLng = selectedIsPickup.value ? pickupLongitude.value || -81.760655 : destinationLongitude.value || -81.760655;

    map = L.map(mapContainer.value).setView([initialLat, initialLng], 13);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    // Si ya hay coords, poner marker
    if (
        (selectedIsPickup.value && pickupLatitude.value && pickupLongitude.value) ||
        (!selectedIsPickup.value && destinationLatitude.value && destinationLongitude.value)
    ) {
      currentMarker = L.marker([initialLat, initialLng]).addTo(map);
      if (selectedIsPickup.value && pickupAddress.value) {
        currentMarker.bindPopup(`${pickupAddress.value}`).openPopup();
      } else if (!selectedIsPickup.value && destinationAddress.value) {
        currentMarker.bindPopup(`${destinationAddress.value}`).openPopup();
      }
    }

    map.on('click', async (e) => {
      const { lat, lng } = e.latlng;
      if (currentMarker) {
        map.removeLayer(currentMarker);
      }
      currentMarker = L.marker([lat, lng]).addTo(map);
      try {
        const data = await getReverseGeocode(lat, lng);
        selectedLocation.value = {
          lat: lat,
          lng: lng,
          address: data.items[0].address.label,
        };
      } catch (error) {
        console.error('Error getting reverse geocode:', error);
      }
    });
  }, 500);
};

const setAddress = () => {
  if (selectedIsPickup.value) {
    pickupAddress.value = selectedLocation.value.address;
    pickupLatitude.value = selectedLocation.value.lat;
    pickupLongitude.value = selectedLocation.value.lng;
    validateField('pickupAddress');
    validateField('pickupLatitude');
    validateField('pickupLongitude');
  } else {
    destinationAddress.value = selectedLocation.value.address;
    destinationLatitude.value = selectedLocation.value.lat;
    destinationLongitude.value = selectedLocation.value.lng;
    validateField('destinationAddress');
    validateField('destinationLatitude');
    validateField('destinationLongitude');
  }
  selectedLocation.value = { lat: 0, lng: 0, address: '' };
  showMap.value = false;
};

// --- ACCIÓN: CREAR VIAJE
const createTripAction = async () => {
  // Validar todos los campos antes de crear
  Object.keys(validateRequiredInputs).forEach((fieldName) => {
    validateField(fieldName);
  });
  if (isCreateTripDisabled.value) return;

  loading.value = true;

  const tripData = {
    pickup: {
      address: pickupAddress.value,
      location: { latitude: pickupLatitude.value, longitude: pickupLongitude.value },
    },
    destination: {
      address: destinationAddress.value,
      location: { latitude: destinationLatitude.value, longitude: destinationLongitude.value },
    },
    price: price.value,
    paymentMethod: paymentMethod.value,
    distance: distance.value,
    duration: duration.value,
    user: {
      name: clientName.value,
      phone: clientPhone.value,
      id: clientId.value,
    },
    driver: selectedDriver.value._id,
    bookingDate: bookingDate.value ? formatISODate(bookingDate.value) : null,
    nearby: notifyNearbyDrivers.value,
    polyline: polyline.value,
  };

  try {
    await createRide(tripData);
    customToast('Trip created successfully!', 'success');
    // Limpiar campos
    pickupAddress.value = 'Terminal access rd, Fort Myers, FL 33913';
    pickupLatitude.value = 26.519211;
    pickupLongitude.value = -81.758514;
    destinationAddress.value = '';
    destinationLatitude.value = 0.0;
    destinationLongitude.value = 0.0;
    price.value = 0.0;
    paymentMethod.value = 'cash';
    distance.value = 0.0;
    duration.value = 0.0;
    clientName.value = '';
    clientPhone.value = '';
    clientId.value = '';
    selectedDriver.value = {};
    bookingDate.value = '';
    durationForHumans.value = '';
    notifyNearbyDrivers.value = false;
  } catch (error) {
    console.error('Error creating trip:', error);
    customToast('Error creating trip', 'error');
  } finally {
    loading.value = false;
  }
};

// --- WATCH PARA CALCULAR LA RUTA
watch([pickupLatitude, pickupLongitude, destinationLatitude, destinationLongitude], async () => {
  if (pickupLatitude.value && pickupLongitude.value && destinationLatitude.value && destinationLongitude.value) {
    let originLonLat = { latitude: pickupLatitude.value, longitude: pickupLongitude.value };
    let destinationLonLat = { latitude: destinationLatitude.value, longitude: destinationLongitude.value };

    try {
      const data = await getRoute(originLonLat, destinationLonLat);
      const json = data.routes[0].sections[0];
      let basePrice = 5;
      let costPerKm = 1.5;
      let distanceInKm = json.summary.length / 1000;
      polyline.value = json.polyline;

      price.value = (
          distanceInKm < 1
              ? basePrice
              : basePrice + (distanceInKm - 1) * costPerKm
      ).toFixed(2);

      distance.value = (json.summary.length * 0.000621371).toFixed(2); // km to miles
      duration.value = json.summary.duration;
      durationForHumans.value = `${Math.floor(json.summary.duration / 3600)}h ${Math.floor((json.summary.duration % 3600) / 60)}m`;
      validateField('price');
    } catch (error) {
      console.error('Error getting route:', error);
    }
  }
});

// --- MANEJADOR DRIVER SELECTION
const handleSelectDriver = (driver) => {
  selectedDriver.value = driver;
};

onMounted(() => {
  // Inicializar touchedFields y fieldErrors
  for (const key in validateRequiredInputs) {
    touchedFields.value[key] = false;
    fieldErrors.value[key] = '';
  }
  // Validar inicialmente
  Object.keys(validateRequiredInputs).forEach((fieldName) => {
    validateField(fieldName);
  });
});
</script>

<template>
  <!--
    Para que la variante dark: funcione, recuerda
    que necesitas tu tailwind.config con darkMode habilitado
    y la clase "dark" aplicada en algún contenedor superior.
  -->
  <div class="min-h-screen bg-transparent text-gray-800 dark:text-gray-100 p-4">

    <!-- Botón de Regreso -->
    <BackButtonComponent url="/trips" />

    <!-- Loading -->
    <LoadingComponent v-if="loading" />

    <div
    id="pickOnMap"
    class="fixed inset-0 z-50 bg-black bg-opacity-70"
    :class="{ 'hidden': !showMap }"
>
  <div class="relative w-full h-full flex flex-col items-center justify-center">

    <!-- Loader del mapa -->
    <div
        v-if="mapLoading"
        class="absolute top-0 left-0 w-full h-full z-50 bg-black bg-opacity-50 flex items-center justify-center"
    >
      <div class="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
    </div>

    <!-- Contenedor del mapa -->
    <div ref="mapContainer" class="w-full h-full relative">
      <div class="absolute inset-0" style="pointer-events: auto;">
        <!-- Aquí puedes cargar tu mapa dinámico -->
      </div>
    </div>

    <!-- Botones Confirmar/Cancelar -->
    <div class="relative bottom-0 w-full p-4 bg-white bg-opacity-90 dark:bg-gray-900 dark:bg-opacity-90 flex justify-center items-center space-x-4 z-50 pointer-events-auto">
      <button
          class="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded font-bold transition-colors"
          @click="showMap = false"
      >
        Cancel
      </button>
      <button
          class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-bold transition-colors"
          @click="setAddress"
      >
        Confirm
      </button>
      <strong v-if="selectedLocation.address" class="ml-4 text-gray-700 dark:text-gray-100">
        {{ selectedLocation.address }}
      </strong>
    </div>
  </div>
</div>


    <!-- Contenido Principal -->
    <div class="flex justify-center items-center py-8">
      <div class="max-w-7xl w-full p-8 rounded-lg shadow-lg bg-white text-gray-800 dark:bg-gray-700 dark:text-gray-100">

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <!-- Pickup Address -->
          <div class="relative">
            <label for="pickup" class="block font-medium mb-1">
              Pickup Address <RequiredComponent name="Pickup address" />
            </label>
            <div class="relative">
              <input
                  id="pickup"
                  type="text"
                  v-model="pickupAddress"
                  :class="[
                  'w-full p-3 rounded focus:outline-none border',
                  touchedFields.pickupAddress && fieldErrors.pickupAddress ? 'border-red-500' : 'border-gray-300 dark:border-gray-600',
                  'bg-gray-50 text-gray-800 dark:bg-gray-700 dark:text-gray-100'
                ]"
                  @focus="showSuggestions('pickup')"
                  @blur="touchedFields.pickupAddress = true; validateField('pickupAddress'); hideSuggestions()"
                  @input="handleInput('pickupAddress'); searchSuggestions('pickup', $event.target.value)"
                  autocomplete="off"
              />
              <div
                  v-if="touchedFields.pickupAddress && fieldErrors.pickupAddress"
                  class="text-red-500 text-sm mt-1"
              >
                {{ fieldErrors.pickupAddress }}
              </div>
              <!-- Sugerencias -->
              <div
                  v-if="suggestionsVisible && activeInputType === 'pickup'"
                  class="absolute left-0 top-full w-full z-50 border border-gray-300 dark:border-gray-600 rounded shadow-lg bg-white dark:bg-gray-700 mt-1"
              >
                <ul class="max-h-48 overflow-auto">
                  <li
                      v-for="suggestion in suggestions.items"
                      :key="suggestion.id"
                      @mousedown="selectSuggestion(suggestion)"
                      class="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-gray-700 dark:text-gray-100"
                  >
                    {{ suggestion.title }} <br> <small v-if="suggestion.address.label" class="text-gray-600 dark:text-gray-200">{{ suggestion.address.label }}</small>
                  </li>
                </ul>
              </div>
            </div>
            <DefaultButton title="Pick on Map" @click="pickOnMap('pickup', true)" />
          </div>

          <!-- Destination Address -->
          <div class="relative">
            <label for="destination" class="block font-medium mb-1">
              Destination Address <RequiredComponent name="Destination address" />
            </label>
            <div class="relative">
              <input
                  id="destination"
                  type="text"
                  v-model="destinationAddress"
                  :class="[
                  'w-full p-3 rounded focus:outline-none border',
                  touchedFields.destinationAddress && fieldErrors.destinationAddress ? 'border-red-500' : 'border-gray-300 dark:border-gray-600',
                  'bg-gray-50 text-gray-800 dark:bg-gray-700 dark:text-gray-100'
                ]"
                  @focus="showSuggestions('destination')"
                  @blur="touchedFields.destinationAddress = true; validateField('destinationAddress'); hideSuggestions()"
                  @input="handleInput('destinationAddress'); searchSuggestions('destination', $event.target.value)"
                  autocomplete="off"
              />
              <div
                  v-if="touchedFields.destinationAddress && fieldErrors.destinationAddress"
                  class="text-red-500 text-sm mt-1"
              >
                {{ fieldErrors.destinationAddress }}
              </div>
              <!-- Sugerencias -->
              <div
                  v-if="suggestionsVisible && activeInputType === 'destination'"
                  class="absolute left-0 top-full w-full z-50 border border-gray-300 dark:border-gray-600 rounded shadow-lg bg-white dark:bg-gray-700 mt-1"
              >
                <ul class="max-h-48 overflow-auto">
                  <li
                      v-for="suggestion in suggestions.items"
                      :key="suggestion.id"
                      @mousedown="selectSuggestion(suggestion)"
                      class="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-gray-700 dark:text-gray-100"
                  >
                    {{ suggestion.title }} <br> <small v-if="suggestion.address.label" class="text-gray-600 dark:text-gray-200">{{ suggestion.address.label }}</small>
                  </li>
                </ul>
              </div>
            </div>
            <DefaultButton title="Pick on Map" @click="pickOnMap('pickup', false)" />
          </div>

          <!-- Client Name -->
          <div class="relative">
            <label for="clientName" class="block font-medium mb-1">
              Client Name <RequiredComponent name="Client name" />
            </label>
            <div class="relative">
              <input
                  id="clientName"
                  type="text"
                  v-model="clientName"
                  :class="[
                  'w-full p-3 rounded focus:outline-none border',
                  touchedFields.clientName && fieldErrors.clientName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600',
                  'bg-gray-50 text-gray-800 dark:bg-gray-700 dark:text-gray-100'
                ]"
                  @focus="showUserSuggestions('name')"
                  @blur="touchedFields.clientName = true; validateField('clientName'); hideUserSuggestions()"
                  @input="handleInput('clientName'); searchUserSuggestions('name', $event.target.value)"
                  autocomplete="off"
              />
              <div v-if="touchedFields.clientName && fieldErrors.clientName" class="text-red-500 text-sm mt-1">
                {{ fieldErrors.clientName }}
              </div>
              <!-- Sugerencias Usuario (Name) -->
              <div
                  v-if="userSuggestionsVisible && activeUserInputType === 'name'"
                  class="absolute left-0 top-full w-full z-50 border border-gray-300 dark:border-gray-600 rounded shadow-lg bg-white dark:bg-gray-700 mt-1"
              >
                <ul class="max-h-48 overflow-auto">
                  <li
                      v-for="suggestion in userSuggestions"
                      :key="suggestion._id"
                      @mousedown="selectUserSuggestion(suggestion)"
                      class="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-gray-700 dark:text-gray-100"
                  >
                    {{ suggestion.name }} - {{ suggestion.phone }}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Client Phone -->
          <div class="relative">
            <label for="clientPhone" class="block font-medium mb-1">
              Client Phone <RequiredComponent name="Client Phone" />
            </label>
            <div class="relative">
              <input
                  id="clientPhone"
                  type="text"
                  v-model="clientPhone"
                  :class="[
                  'w-full p-3 rounded focus:outline-none border',
                  touchedFields.clientPhone && fieldErrors.clientPhone ? 'border-red-500' : 'border-gray-300 dark:border-gray-600',
                  'bg-gray-50 text-gray-800 dark:bg-gray-700 dark:text-gray-100'
                ]"
                  @focus="showUserSuggestions('phone')"
                  @blur="touchedFields.clientPhone = true; validateField('clientPhone'); hideUserSuggestions()"
                  @input="handleInput('clientPhone'); searchUserSuggestions('phone', $event.target.value)"
                  autocomplete="off"
              />
              <div v-if="touchedFields.clientPhone && fieldErrors.clientPhone" class="text-red-500 text-sm mt-1">
                {{ fieldErrors.clientPhone }}
              </div>
              <!-- Sugerencias Usuario (Phone) -->
              <div
                  v-if="userSuggestionsVisible && activeUserInputType === 'phone'"
                  class="absolute left-0 top-full w-full z-50 border border-gray-300 dark:border-gray-600 rounded shadow-lg bg-white dark:bg-gray-700 mt-1"
              >
                <ul class="max-h-48 overflow-auto">
                  <li
                      v-for="suggestion in userSuggestions"
                      :key="suggestion._id"
                      @mousedown="selectUserSuggestion(suggestion)"
                      class="p-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-gray-700 dark:text-gray-100"
                  >
                    {{ suggestion.phone }} - {{ suggestion.name }}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Driver -->
          <div>
            <FilterDriverComponent
                :selectedDriver="selectedDriver"
                :onSelectDriver="handleSelectDriver"
            />
          </div>

          <!-- Price -->
          <div>
            <label for="price" class="block font-medium mb-1">
              Price <RequiredComponent name="Price" />
            </label>
            <input
                id="price"
                type="number"
                v-model="price"
                :class="[
                'w-full p-3 rounded focus:outline-none border',
                touchedFields.price && fieldErrors.price ? 'border-red-500' : 'border-gray-300 dark:border-gray-600',
                'bg-gray-50 text-gray-800 dark:bg-gray-700 dark:text-gray-100'
              ]"
                @blur="touchedFields.price = true; validateField('price')"
                @input="handleInput('price')"
            />
            <div v-if="touchedFields.price && fieldErrors.price" class="text-red-500 text-sm mt-1">
              {{ fieldErrors.price }}
            </div>
          </div>

          <!-- Payment Method -->
          <div>
            <label for="paymentMethod" class="block font-medium mb-1">
              Payment Method <RequiredComponent name="Payment method" />
            </label>
            <select
                id="paymentMethod"
                v-model="paymentMethod"
                :class="[
                'w-full p-3 rounded focus:outline-none border',
                touchedFields.paymentMethod && fieldErrors.paymentMethod ? 'border-red-500' : 'border-gray-300 dark:border-gray-600',
                'bg-gray-50 text-gray-800 dark:bg-gray-700 dark:text-gray-100'
              ]"
                @blur="touchedFields.paymentMethod = true; validateField('paymentMethod')"
                @change="handleInput('paymentMethod')"
            >
              <option value="cash">Cash</option>
              <option value="square">Square</option>
            </select>
            <div v-if="touchedFields.paymentMethod && fieldErrors.paymentMethod" class="text-red-500 text-sm mt-1">
              {{ fieldErrors.paymentMethod }}
            </div>
          </div>

          <!-- Distance -->
          <div>
            <label for="distance" class="block font-medium mb-1">
              Distance (Miles)
            </label>
            <input
                id="distance"
                type="number"
                v-model="distance"
                class="w-full p-3 rounded bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-100 border border-gray-300 dark:border-gray-600 focus:outline-none"
                readonly
            />
          </div>

          <!-- Duration -->
          <div>
            <label for="duration" class="block font-medium mb-1">
              Duration (Hrs/Mins)
            </label>
            <input
                id="duration"
                type="text"
                v-model="durationForHumans"
                class="w-full p-3 rounded bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-100 border border-gray-300 dark:border-gray-600 focus:outline-none"
                readonly
            />
          </div>

          <!-- Booking Date -->
          <div>
            <label for="bookingDate" class="block font-medium mb-1">
              Booking Date
            </label>
            <input
                id="bookingDate"
                type="datetime-local"
                v-model="bookingDate"
                :class="[
                'w-full p-3 rounded focus:outline-none border',
                touchedFields.bookingDate ? 'border-gray-300 dark:border-gray-600' : 'border-gray-300 dark:border-gray-600',
                'bg-gray-50 text-gray-800 dark:bg-gray-700 dark:text-gray-100'
              ]"
                @blur="touchedFields.bookingDate = true; validateField('bookingDate')"
                @change="handleInput('bookingDate')"
            />
          </div>

          <!-- Notify Nearby Drivers -->
          <div class="flex items-center space-x-2 mt-4">
            <input
                type="checkbox"
                v-model="notifyNearbyDrivers"
                id="notify-checkbox"
                class="w-5 h-5 text-blue-600 bg-gray-50 border border-gray-300 rounded focus:ring-blue-500 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 dark:focus:ring-blue-600"
            />
            <label for="notify-checkbox" class="cursor-pointer select-none">
              Notify Nearby Drivers
            </label>
          </div>
        </div>

        <!-- Botón Confirmar -->
        <div class="text-center mt-8">

          <DefaultButton 
          @click="createTripAction" 
          :title="bookingDate ? 'Booking Trip' : 'Confirm Trip'" 
          :disabled="isCreateTripDisabled" />

        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Ajustes del mapa */
#pickOnMap {
  /* Con .fixed.inset-0 y bg-black bg-opacity-70 ya cubrimos la pantalla */
}

/* Ajuste extra para que el dropdown aparezca inmediatamente debajo de cada input */
.relative .absolute {
  margin-top: 2px; /* Pequeño margen para separar el borde */
}

/* Borde de error (ejemplo si no quieres usar dark:border-red-500) */
.border-red-500 {
  border-color: #f56565 !important;
}
</style>
